import { Activity, GitBranch, ShieldCheck, Zap } from 'lucide-react';
import { cn } from '../lib/utils';
import { useGraphStore } from '../store/graphStore';
import type { GraphMode } from '../types/graph';

interface ModeOption {
  value: GraphMode;
  label: string;
  icon: React.ReactNode;
}

const modes: ModeOption[] = [
  { value: 'activity', label: 'Activity', icon: <Activity size={16} /> },
  { value: 'blast-radius', label: 'Blast Radius', icon: <Zap size={16} /> },
  { value: 'policy-coverage', label: 'Policy Coverage', icon: <ShieldCheck size={16} /> },
  { value: 'correlations', label: 'Correlations', icon: <GitBranch size={16} /> },
];

interface GraphModeSelectorProps {
  onModeChange: (mode: GraphMode) => void;
}

export function GraphModeSelector({ onModeChange }: GraphModeSelectorProps) {
  const { activeMode, isModeLoading } = useGraphStore();

  const handleClick = (mode: GraphMode) => {
    if (mode === activeMode || isModeLoading) return;
    onModeChange(mode);
  };

  return (
    <div className="flex items-center gap-1 rounded-full border border-border/50 bg-background/80 backdrop-blur-md p-1 shadow-lg">
      {modes.map((mode) => (
        <button
          key={mode.value}
          onClick={() => handleClick(mode.value)}
          disabled={isModeLoading}
          className={cn(
            'flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 lg:px-4 py-1.5 text-sm font-medium transition-all duration-200',
            activeMode === mode.value
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
            isModeLoading && 'opacity-60 cursor-not-allowed'
          )}
        >
          {mode.icon}
          {mode.label}
        </button>
      ))}
    </div>
  );
}
